import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth, canViewActual } from "../lib/auth";
import { PageHeader, Section } from "../components/Primitives";
import { fmtMoney } from "../lib/money";
import { MapPin, Layers, BarChart3 } from "lucide-react";

const fmtNum = (v) => (v || 0).toLocaleString("en-US");

export default function ManagementDashboard() {
    const { user } = useAuth();
    const showInternal = canViewActual(user);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let ignore = false;
        api.get("/management/regional-stats").then((r) => { if (!ignore) setData(r.data); }).finally(() => { if (!ignore) setLoading(false); });
        return () => { ignore = true; };
    }, []);

    const regions = data?.regions || [];
    const totals = data?.totals || { impressions: 0, clicks: 0 };
    const platforms = data?.platforms || [];
    const allowedRegions = Array.isArray(user?.allowed_regions) ? user.allowed_regions : [];
    const totalCtr = useMemo(() => totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0, [totals]);

    return (
        <div className="p-6 max-w-7xl mx-auto" data-testid="management-dashboard">
            <PageHeader
                eyebrow="MANAGEMENT"
                title={`Welcome, ${user?.full_name || ""}`}
                subtitle={`Region-wise rollup for ${allowedRegions.join(", ") || "your assigned regions"}.`}
            />

            {/* Top metrics — total impressions + clicks */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white border border-zinc-200 rounded p-5" data-testid="mgmt-total-impressions">
                    <div className="overline text-zinc-400">Total impressions</div>
                    <div className="font-display text-3xl tabular-nums text-zinc-900 mt-1">{fmtNum(totals.impressions)}</div>
                </div>
                <div className="bg-white border border-zinc-200 rounded p-5" data-testid="mgmt-total-clicks">
                    <div className="overline text-zinc-400">Total clicks</div>
                    <div className="font-display text-3xl tabular-nums wl-accent-text mt-1">{fmtNum(totals.clicks)}</div>
                    <div className="text-[11px] text-zinc-500 mt-1">{totalCtr.toFixed(2)}% CTR</div>
                </div>
                <div className="bg-white border border-zinc-200 rounded p-5" data-testid="mgmt-region-count">
                    <div className="overline text-zinc-400">Regions in scope</div>
                    <div className="font-display text-3xl tabular-nums text-zinc-900 mt-1">{regions.length}</div>
                    <div className="text-[11px] text-zinc-500 mt-1">{allowedRegions.slice(0, 3).join(", ") || "—"}</div>
                </div>
            </div>

            {/* Per-platform total spend + margin */}
            <Section title="Platform spend & margin" testId="section-platform-totals">
                {platforms.length === 0 ? (
                    <div className="bg-white border border-zinc-200 p-8 text-center text-zinc-500 text-sm">
                        {loading ? "Loading…" : "No platform data for this window."}
                    </div>
                ) : (
                    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(320px, 1fr))` }}>
                        {platforms.map((p) => {
                            const label = p.platform === "meta" ? "Meta" : p.platform === "dv360" ? "DV360" : p.platform;
                            const currencies = Array.isArray(p.currencies) ? p.currencies : [];
                            return (
                                <div key={p.platform} className="bg-white border border-zinc-200 rounded p-5" data-testid={`mgmt-platform-card-${p.platform}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="overline">{label}</div>
                                        <Layers className="h-4 w-4 text-zinc-400" />
                                    </div>
                                    {currencies.length === 0 ? (
                                        <div className="text-xs text-zinc-400">No data</div>
                                    ) : (
                                        <div className="space-y-3">
                                            {currencies.map((c) => (
                                                <div key={c.currency} className="border-t border-zinc-100 pt-3 first:border-t-0 first:pt-0" data-testid={`mgmt-platform-${p.platform}-${c.currency}`}>
                                                    <div className="flex items-baseline justify-between mb-2">
                                                        <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-700 whitespace-nowrap">{c.currency}</span>
                                                        <span className="text-[10px] uppercase tracking-wider text-zinc-400">Spend &amp; Margin</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <div className="text-[10px] uppercase tracking-wider text-zinc-400">Total spend</div>
                                                            <div className={`font-display text-base tabular-nums whitespace-nowrap ${showInternal ? "wl-internal" : "text-zinc-900"}`}>
                                                                {fmtMoney(showInternal ? (c.spend || 0) : (c.display_spend || 0), c.currency)}
                                                            </div>
                                                        </div>
                                                        {showInternal && (
                                                            <div>
                                                                <div className="text-[10px] uppercase tracking-wider text-amber-500">Margin</div>
                                                                <div className={`font-display text-base tabular-nums whitespace-nowrap ${(c.margin || 0) >= 0 ? "text-amber-500" : "text-red-500"}`}>
                                                                    {fmtMoney(c.margin || 0, c.currency)}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Section>

            {/* Per-region cards */}
            <Section title="Region performance" testId="section-regions">
                {regions.length === 0 ? (
                    <div className="bg-white border border-zinc-200 p-8 text-center text-zinc-500 text-sm">
                        {loading ? "Loading…" : "No region data — make sure your advertisers have a country set."}
                    </div>
                ) : (
                    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(380px, 1fr))` }}>
                        {regions.map((r) => {
                            const currencies = Array.isArray(r.currencies) ? r.currencies : [];
                            return (
                            <div key={r.region} className="bg-white border border-zinc-200 rounded p-5" data-testid={`mgmt-region-card-${r.region}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="overline flex items-center gap-1.5">
                                        <MapPin className="h-3.5 w-3.5 text-zinc-500" />
                                        {r.region}
                                    </div>
                                    <BarChart3 className="h-4 w-4 text-zinc-400" />
                                </div>

                                {/* Campaign counts */}
                                <div className="grid grid-cols-3 gap-x-3 gap-y-4 mb-4">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-zinc-400 whitespace-nowrap">Active</div>
                                        <div className="font-display text-xl tabular-nums text-zinc-900 mt-0.5">{fmtNum(r.active_count)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-zinc-400 whitespace-nowrap">Ongoing</div>
                                        <div className="font-display text-xl tabular-nums wl-accent-text mt-0.5">{fmtNum(r.ongoing_count)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-zinc-400 whitespace-nowrap">Delivered</div>
                                        <div className="font-display text-xl tabular-nums text-zinc-700 mt-0.5">{fmtNum(r.delivered_count)}</div>
                                    </div>
                                </div>

                                {/* Per-currency CPM + CTR */}
                                <div className="pt-3 border-t border-zinc-200">
                                    {currencies.length === 0 ? (
                                        <div className="text-xs text-zinc-400">No spend yet</div>
                                    ) : (
                                        <div className="space-y-2.5">
                                            {currencies.map((c) => (
                                                <div key={c.currency} className="flex items-center justify-between gap-3">
                                                    <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-700 whitespace-nowrap">{c.currency}</span>
                                                    <div className="flex items-baseline gap-5 flex-1 justify-end">
                                                        <div className="text-right">
                                                            <div className="text-[10px] uppercase tracking-wider text-zinc-400">Avg CPM</div>
                                                            <div className="font-display text-base tabular-nums text-zinc-900 whitespace-nowrap">{fmtMoney(c.cpm, c.currency)}</div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-[10px] uppercase tracking-wider text-zinc-400">Avg CTR</div>
                                                            <div className="font-display text-base tabular-nums wl-accent-text">{(c.ctr || 0).toFixed(2)}%</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </Section>
        </div>
    );
}
